import React, { forwardRef, memo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import FileUploaderUppy, {
  AttachmentValue,
  UploaderView,
} from '@lib/components/FileUploaderUppy/FileUploaderUppy';
import {
  FileSizeLimits,
  UppyImageAllowedFileTypes,
} from '@lib/enums/mimeTypes';
import Placeholder from '@lib/assets/images/preview-placeholder.svg';
import Image from '@lib/components/Image/Image';
import { materialSymbolsOutlined } from '@lib/enums/common';
import Typography from '@lib/components/Typography/Typography';
import styles from './ImageUploader.module.scss';

export enum ImageUploaderMode {
  Full = 'full',
  Compact = 'compact',
}

export type ImageUploaderProps = {
  className?: string;
  onChange: (variables: unknown) => void;
  value?: AttachmentValue;
  mode?: ImageUploaderMode;
  label?: string;
  allowedFileTypes?: string[];
  maxFileSize?: number;
  note?: string;
  maxImageDimensions?: { width: number; height: number };
  requireSquare?: boolean;
};

const ImageUploader = forwardRef(
  (props: ImageUploaderProps, ref: React.ForwardedRef<HTMLInputElement>) => {
    const {
      className,
      value,
      onChange,
      mode = 'full',
      label,
      allowedFileTypes = UppyImageAllowedFileTypes,
      maxFileSize,
      note: noteProp,
      maxImageDimensions,
      requireSquare,
    } = props;
    const url = value?.urls?.large || value?.urls?.original || value?.url;
    const { t } = useTranslation();
    const note =
      noteProp ??
      t('image-upload-validation-msg', { size: FileSizeLimits.Image });
    const isCompact = mode === ImageUploaderMode.Compact;

    const uploadButton = ({ onClick }: { onClick: () => void }) => (
      <Button
        onClick={onClick}
        buttonText={url ? t('edit-image') : t('upload-image')}
        buttonType={ButtonTypes.primaryOutlined}
        leftIcon={isCompact ? 'upload' : 'edit'}
        leftIconClassName={materialSymbolsOutlined}
        buttonSize={ButtonSizes.small}
      />
    );

    if (isCompact) {
      return (
        <>
          {label && (
            <Typography variant="body" strong className={styles.label}>
              {label}
            </Typography>
          )}
          <div className={classNames(styles.compactContainer, className)}>
            <div className={styles.compactImageWrapper}>
              {(url || Placeholder) && (
                <Image
                  src={url || Placeholder}
                  className={styles.compactImage}
                />
              )}
            </div>
            <FileUploaderUppy
              allowedFileTypes={allowedFileTypes}
              maxFileSize={maxFileSize}
              maxImageDimensions={maxImageDimensions}
              requireSquare={requireSquare}
              openButton={uploadButton}
              ref={ref}
              onChange={onChange}
              type={UploaderView.modal}
              note={note}
            />
            {url && (
              <Button
                buttonText={t('delete')}
                buttonType={ButtonTypes.negativeOutlined}
                leftIcon="delete"
                leftIconClassName={materialSymbolsOutlined}
                buttonSize={ButtonSizes.small}
                onClick={() => onChange(null)}
              />
            )}
          </div>
        </>
      );
    }

    return (
      <>
        {label && (
          <Typography variant="body" strong className={styles.label}>
            {label}
          </Typography>
        )}
        <div className={className}>
          <div className={styles.img}>
            {(url || Placeholder) && <Image src={url || Placeholder} />}
          </div>
          <div className={styles.body}>
            <FileUploaderUppy
              allowedFileTypes={allowedFileTypes}
              maxFileSize={maxFileSize}
              maxImageDimensions={maxImageDimensions}
              requireSquare={requireSquare}
              openButton={uploadButton}
              ref={ref}
              onChange={onChange}
              type={UploaderView.modal}
              note={note}
            />
            {url !== undefined && (
              <Button
                buttonText={t('delete')}
                buttonType={ButtonTypes.negativeOutlined}
                leftIcon="delete"
                leftIconClassName={materialSymbolsOutlined}
                buttonSize={ButtonSizes.small}
                onClick={() => onChange(null)}
              />
            )}
          </div>
        </div>
      </>
    );
  },
);

export default memo(ImageUploader);
